<script setup lang="ts">
/**
 * Base error page template shared by all concrete error pages.
 *
 * Props
 *   code        — stable application error code shown at the bottom of the
 *                 actions list (e.g. `BACKEND_10001`); users copy it for support.
 *   title       — optional main heading (hidden when blank).
 *   explanation — paragraph describing what went wrong.
 *   actions     — optional ordered list of recommended steps; a final
 *                 "give error code to your administrator" item is always appended.
 *
 * Slot (default)
 *   Action buttons rendered below the actions list — typically a "Go To Login"
 *   button. Concrete error pages fill this slot.
 *
 * Layout: SadSmileIcon → title → "The error occurred!" banner →
 *         Explanation → Recommended Actions list → slot.
 */
import SadSmileIcon from '@/components/helpers/SadSmileIcon.vue'
import { useIsMobile } from '@/composables/useIsMobile'

withDefaults(
  defineProps<{
    /** Stable application error code (e.g. BACKEND_10001, CLIENT_40401). */
    code: string
    /** Main heading (e.g. "Oops! The page not found!"). */
    title: string
    /** Body text explaining what went wrong. */
    explanation: string
    /** Optional list of recommended actions (numbered list). */
    actions?: readonly string[]
  }>(),
  { actions: () => [] },
)

const { isMobile } = useIsMobile()
</script>

<template>
  <article :class="{ 'article--mobile': isMobile }">
    <div :class="{ 'err-body--mobile': isMobile }">
      <SadSmileIcon
        class="error-view-emoji"
        :size="isMobile ? '80px' : 'var(--size-error-emoji)'"
      />
      <h1 v-if="title.trim().length > 0" class="font-tufte text-tufte-text">{{ title }}</h1>
      <p class="error-occurred-banner font-tufte text-tufte-text">The error occurred!</p>
      <div v-if="isMobile" class="slot-inline--mobile">
        <slot />
      </div>
      <h2>Explanation</h2>
      <p class="font-tufte text-tufte-text">
        {{ explanation }}
      </p>
      <h2>Recommended Actions</h2>
      <ol
        :class="
          isMobile
            ? 'error-view-actions--mobile font-tufte text-tufte-text'
            : 'error-view-actions font-tufte text-tufte-text'
        "
        type="1"
      >
        <li v-for="(item, i) in actions" :key="i">{{ item }}</li>
        <li>
          If the same error repeats in sequence, give the error code (<strong>{{ code }}</strong
          >) to your administrator or write to support.
        </li>
      </ol>
      <slot v-if="!isMobile" />
    </div>
  </article>
</template>

<style scoped>
.error-view-emoji {
  margin-bottom: 0.75rem;
}

.error-occurred-banner {
  margin: 0.5rem 0 1rem;
}

.error-view-actions {
  list-style-type: decimal;
  padding-left: 1.5rem;
}

/* ── mobile layout ─────────────────────────────────────────────────────────── */

.article--mobile {
  min-height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.err-body--mobile {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 28px 22px 20px;
}

.err-body--mobile .error-view-emoji {
  margin-bottom: 0.65rem;
  color: var(--color-money-negative);
}

.err-body--mobile h1 {
  font-size: 1.65rem;
  line-height: 1.1;
  letter-spacing: -0.01em;
  font-weight: 400;
  margin-bottom: 0.3rem;
}

.err-body--mobile .error-occurred-banner {
  font-size: 0.95rem;
  margin: 0 0 0.75rem;
}

.err-body--mobile h2 {
  font-style: italic;
  font-size: 1.1rem;
  margin-bottom: 0.3rem;
}

.err-body--mobile p:not(.error-occurred-banner) {
  font-size: 0.95rem;
  line-height: 1.4;
  margin-bottom: 0.9rem;
}

.error-view-actions--mobile {
  list-style: none;
  counter-reset: step;
  padding-left: 0;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.error-view-actions--mobile li {
  counter-increment: step;
  position: relative;
  padding-left: 1.6rem;
  font-size: 0.95rem;
  line-height: 1.35;
}

.error-view-actions--mobile li::before {
  content: counter(step) '.';
  position: absolute;
  left: 0;
  font-variant-numeric: tabular-nums;
  color: var(--color-tufte-muted);
}

.slot-inline--mobile {
  margin-bottom: 0.75rem;
}

.slot-inline--mobile :deep(.tufte-button) {
  font-size: 1rem;
  border-radius: 8px;
  padding: 0.7rem 1rem;
  text-align: center;
  width: 100%;
}
</style>
